<template>
  <div class="clara-onboarding">
    <ClaraIntroModal
      :open.sync="showModal"
      @close="handleModalClose"
      @start="handleModalStart"
    />

    <ClaraIntroSpotlight
      :open="showSpotlight"
      :target="LAUNCHER_SELECTOR"
      @finish="handleSpotlightFinish"
    />
  </div>
</template>

<script>
import { ACCESS_VALUES } from '../../../../common/constants/global';
import { OPTIONS_STORE_ACCESS } from '@/vuex/module/access';
import { OPTIONS_STORE_RELEASES } from '@/vuex/module/releases';

import ClaraIntroModal from './ClaraIntroModal.vue';
import ClaraIntroSpotlight from './ClaraIntroSpotlight.vue';

import claraGuideStorage from '@/utils/claraGuideStorage';

/**
 * Guia de apresentação da Clara.
 *
 * Ele NÃO tem flag própria: a porta é a mesma flag de "nova versão" que a área
 * de Novidades já calcula (release mais recente com menos de 14 dias). Quando a
 * versão deixa de ser nova, o guia some sozinho — sem ninguém precisar lembrar
 * de desligar nada.
 *
 * Além disso o guia só aparece para quem tem acesso à Clara: apontar um balão
 * para um botão que o usuário não enxerga seria pior do que não mostrar nada.
 */
const LAUNCHER_SELECTOR = '[data-test="btn-ai-assistant"]';
const AI_ASSISTANT_ACCESS = 'AI_ASSISTANT';

export default {
  name: 'ClaraOnboardingGuide',

  components: {
    ClaraIntroModal,
    ClaraIntroSpotlight
  },

  data() {
    return {
      LAUNCHER_SELECTOR,
      showModal: false,
      showSpotlight: false,
      /* O Menu é remontado por `:key` a cada renovação de token, e isso refaz a
         consulta de releases. Sem esta trava o guia reabriria no meio do uso. */
      alreadyEvaluated: false
    };
  },

  computed: {
    hasRecentRelease() {
      return this.$store.getters[OPTIONS_STORE_RELEASES.GETTERS.HAS_RECENT_RELEASE];
    },

    hasClaraAccess() {
      const access = this.$store.getters[OPTIONS_STORE_ACCESS.GETTERS.GET_ACCESS] || [];
      const entry = access.find((item) => item.code === AI_ASSISTANT_ACCESS);

      return Boolean(entry) && entry.value !== ACCESS_VALUES.HIDDEN;
    },

    canShowGuide() {
      return this.hasRecentRelease && this.hasClaraAccess;
    }
  },

  watch: {
    canShowGuide: {
      immediate: true,
      handler() {
        this.evaluate();
      }
    },

    /* O guia é montado em App.vue já na tela de login, quando o topbar ainda
       não existe. Reavaliar a cada navegação dá a ele uma nova chance assim que
       a tela com o botão da Clara entra. */
    $route() {
      this.evaluate();
    }
  },

  methods: {
    /* A flag de release chega por API e os acessos chegam por outra: qualquer
       uma das duas pode ser a última. Por isso a decisão é reavaliada a cada
       mudança, em vez de ser tomada uma única vez no mounted. */
    evaluate() {
      if (this.alreadyEvaluated || !this.canShowGuide) {
        return;
      }

      if (claraGuideStorage.wasDismissedToday()) {
        this.alreadyEvaluated = true;

        return;
      }

      /* Esperamos o botão da Clara estar VISÍVEL antes de abrir o modal: sem
         alvo não há para onde apontar depois do "Conhecer agora". Não basta
         existir no DOM — o v-access esconde por display/visibility, deixando o
         elemento lá. */
      this.$nextTick(() => {
        if (!this.findVisibleLauncher()) {
          return;
        }

        this.alreadyEvaluated = true;
        this.showModal = true;
      });
    },

    findVisibleLauncher() {
      const element = document.querySelector(LAUNCHER_SELECTOR);

      if (!element) {
        return null;
      }

      const { width, height } = element.getBoundingClientRect();
      const visible = width > 0 && height > 0 && window.getComputedStyle(element).visibility !== 'hidden';

      return visible ? element : null;
    },

    handleModalClose({ dontShowToday }) {
      if (dontShowToday) {
        claraGuideStorage.dismissForToday();
      }
    },

    handleModalStart({ dontShowToday }) {
      if (dontShowToday) {
        claraGuideStorage.dismissForToday();
      }

      this.showSpotlight = true;
    },

    /* Quem chegou ao fim do guia já viu o que precisava. Repetir tudo no
       próximo login do mesmo dia seria ruído, então marcamos como visto. */
    handleSpotlightFinish() {
      this.showSpotlight = false;
      claraGuideStorage.dismissForToday();
    }
  }
};
</script>
